import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react'
import type { Session, User } from '@supabase/supabase-js'
import { supabase } from './supabase'
import { identifyUser, markInternal } from './analytics'

// Who the admin IS lives in the database, in public.lyh_is_admin(), and nowhere
// in this bundle. It used to be a string constant here, which published the one
// address that unlocks the approval queue to every visitor who opened the JS:
// a free head start for anyone wanting to phish it or hammer the magic-link
// endpoint with it.
//
// The flag below still only decides whether a button renders, which is what it
// always did. RLS is the enforcement, so someone who flips isAdmin in their own
// devtools gets a queue that returns no rows and writes the server refuses.
// What changes is that they no longer get the address for free on the way in.

interface AuthContextValue {
  session: Session | null
  user: User | null
  isAdmin: boolean
  loading: boolean
  signInWithEmail: (email: string) => Promise<{ error: string | null }>
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)
  const [isAdmin, setIsAdmin] = useState(false)

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session)
      setLoading(false)
    })
    const { data: sub } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s)
    })
    return () => sub.subscription.unsubscribe()
  }, [])

  const user = session?.user ?? null
  const userId = user?.id ?? null
  const userEmail = user?.email ?? null

  // Tie every browser this account signs into to ONE PostHog person. identifyUser()
  // existed in analytics.ts from the start but nothing ever called it, so each browser
  // stayed a separate anonymous visitor: Chrome on iOS on the home network alone
  // showed up as 33 different ids across 90 days (two iOS versions among them, so
  // likely two phones). A browser that never signs in still stays its own id, since
  // nothing links it to the account.
  useEffect(() => {
    if (userId) identifyUser({ id: userId, email: userEmail })
  }, [userId, userEmail])

  // Ask the server once per signed-in user, not on every render: the answer
  // cannot change inside a session, and a signed-out visitor never asks at all.
  // Anything other than an explicit true leaves the queue hidden, so a failed
  // call degrades to the ordinary visitor's view rather than a half-rendered
  // admin one.
  useEffect(() => {
    if (!userId) {
      setIsAdmin(false)
      return
    }
    let cancelled = false
    supabase.rpc('lyh_is_admin').then(({ data, error }) => {
      if (cancelled) return
      if (error) console.error('admin check', error)
      setIsAdmin(data === true)
      // The admin is Andrew, so any browser that signs in as the admin is one of
      // his. Tag it on the spot, which keeps it out of the analytics on every
      // network without anyone having to remember a device list. Keyed on the
      // server's answer, not on anything in this bundle, so it cannot mis-tag a
      // visitor who flips isAdmin in their own devtools.
      if (data === true) markInternal()
    })
    return () => {
      cancelled = true
    }
  }, [userId])

  const signInWithEmail = async (email: string) => {
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: window.location.origin },
    })
    return { error: error?.message ?? null }
  }

  const signOut = async () => {
    await supabase.auth.signOut()
  }

  return (
    <AuthContext.Provider
      value={{ session, user, isAdmin, loading, signInWithEmail, signOut }}
    >
      {children}
    </AuthContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
